import React, { useState, useRef, useEffect, useContext } from 'react';
import { 
  Button,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  Badge
} from '@fluentui/react-components';
import { Text } from '@fluentui/react';
import {
  ChevronLeft24Regular,
  ChevronRight24Regular,
  Filter24Regular,
  WeatherMoon24Regular,
  WeatherSunny24Regular,
  Grid24Regular,
  Dismiss24Regular,
  Alert24Regular,
  Bot24Regular,
  Circle24Filled,
  Edit24Regular,
  DismissCircle24Regular
} from '@fluentui/react-icons';
import CaseTile from './CaseTile';
import './styles/statusbar.css';
import blitzSessionContext from '../../../../context/blitzsession/blitzSessionContext';
import { getReportStatusConfig } from './helper/BlitzSessionHelper';
import CustomToolTipHost from '../../../../components/CustomToolTipHost/CustomToolTipHost';
import EditPatientModal from '../EditPatient/EditPatientModal';
import userContext from '../../../../context/users/UserContext';
import { USERTYPES } from '../../../../constants/Constants';
import CloseStudy from '../Actions/CloseStudy';

const StatusBar = ({
  onClose,
  onThemeToggle
}) => {
  const [showQueueOverlay, setShowQueueOverlay] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showEditPatientModal, setShowEditPatientModal] = useState(false);
  const [showCloseStudyModal, setShowCloseStudyModal] = useState(false);
  const [filter, setFilter] = useState('all');
  const [currentAIAction, setCurrentAIAction] = useState('');
  const [aiNotifications, setAiNotifications] = useState([]);
  const queueButtonRef = useRef(null);
  const notificationButtonRef = useRef(null);
  const overlayRef = useRef(null);
  const notificationOverlayRef = useRef(null);

  const { isDarkTheme, 
    caseQueue, 
    processedQueue, 
    currentStudy, 
    goToNextStudy,
    currentlyLoading,
    loadingStates,
    isTransitioning,
    studyCompletionStatus,
    loadingQueue,
    isLoading,
    isQueueRefreshingRef,
    handleCurrentStudyUpdate,
    iframeOrder,
    sessionId,
    studyBlitzFilter, setStudyBlitzFilter } = useContext(blitzSessionContext);

  const { roles, qcOutsourceLogin } = useContext(userContext);

  const currentData = {
    row: {
      original: currentStudy
    }
  };

  const allCases = [
    ...processedQueue.map(c => ({ ...c, listType: "processed" })),
    ...(currentStudy ? [{ ...currentStudy, listType: "current" }] : []),
    ...caseQueue.map(c => ({ ...c, listType: "upcoming" })),
  ];

  const getStudyById = (studyId) => {
    return allCases.find(study => study.studyId === studyId);
  };

  const filteredCases = allCases.filter(dicomCase => {
    if (filter === "normal") return dicomCase.priority === "normal";
    if (filter === "abnormal") return dicomCase.priority === "abnormal";
    return true;
  });

  // Combined notifications (AI + system)
  const systemNotifications = [
    // { id: '1', type: 'urgent', message: 'Critical case PT004 requires immediate attention', time: '2 min ago' },
    // { id: '2', type: 'warning', message: 'Queue processing delayed - high volume', time: '10 min ago' }
  ];

  const allNotifications = [...aiNotifications, ...systemNotifications];
  const unreadCount = allNotifications.filter(n => n.type === 'urgent' || n.type === 'warning').length;

  const totalStudies = processedQueue.length + (currentStudy ? 1 : 0) + caseQueue.length;
  const currentNumber = processedQueue.length + 1;

  const addAiNotification = (message, type = 'success') => {
    const now = new Date();
    const timeStr = now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    setAiNotifications(prev => [{
      id: `ai-${Date.now()}`,
      message,
      time: timeStr,
      type
    }, ...prev.slice(0, 9)]);
  };

  useEffect(() => {
    if (currentlyLoading && !currentAIAction) {
      const studyInfo = allCases.find(study => study.studyId === currentlyLoading);
      
      if (studyInfo) {
        setCurrentAIAction(`Preloading ${studyInfo.modality} study for ${studyInfo.patientName}`);
      } else {
        setCurrentAIAction(`Preloading study ${currentlyLoading}`);
      }
    } else if (!currentlyLoading && currentAIAction.includes('Preloading')) {
      const completedStudy = currentAIAction.match(/Preloading (.+) study for (.+)/);
      if (completedStudy) {
        addAiNotification(`Preloading completed: ${completedStudy[1]} study for ${completedStudy[2]}`);
      } else {
        addAiNotification('Study preloading completed');
      }
      setCurrentAIAction('');
    }
  }, [currentlyLoading, currentAIAction, caseQueue, currentStudy]);

  useEffect(() => {
    if (isTransitioning && !currentAIAction) {
      setCurrentAIAction('Transitioning to next case...');
    } else if (!isTransitioning && currentAIAction === 'Transitioning to next case...') {
      addAiNotification('Successfully navigated to next case');
      setCurrentAIAction('');
    }
  }, [isTransitioning, currentAIAction]);

  // useEffect(() => {
  //   console.log("queue refresh 123", isQueueRefreshingRef);
  //   const checkRefreshState = setInterval(() => {
  //     if (isQueueRefreshingRef?.current && !currentAIAction.includes('Refreshing')) {
  //       setCurrentAIAction('Refreshing queue from server...');
  //     } else if (!isQueueRefreshingRef?.current && currentAIAction === 'Refreshing queue from server...') {
  //       addAiNotification(`Queue refreshed - ${caseQueue.length} cases available`);
  //       setCurrentAIAction('');
  //     }
  //   }, 500); // Check every 500ms for state changes

  //   return () => clearInterval(checkRefreshState);
  // }, [currentAIAction, caseQueue.length, isQueueRefreshingRef]);

  useEffect(() => {
    const loadingIframes = Object.entries(loadingStates).filter(([_, isLoading]) => isLoading);
    
    if (loadingIframes.length > 0 && !currentAIAction.includes('Loading viewer')) {
      const loadingStudyIds = iframeOrder.filter((studyId, index) => 
        studyId !== -1 && loadingStates[index]
      );
      
      if (loadingStudyIds.length > 0) {
        const studyInfo = getStudyById(loadingStudyIds[0]);
        if (studyInfo) {
          setCurrentAIAction(`Loading viewer for ${studyInfo.patientName}`);
        } else {
          setCurrentAIAction(`Loading viewer components (${loadingIframes.length} active)`);
        }
      }
    } else if (loadingIframes.length === 0 && currentAIAction.includes('Loading viewer')) {
      // addAiNotification('All viewer components loaded successfully');
      setCurrentAIAction('');
    }
  }, [loadingStates, currentAIAction, iframeOrder]);

  // Track study completion status
  // useEffect(() => {
  //   const completedStudies = Object.keys(studyCompletionStatus).filter(studyId => studyCompletionStatus[studyId]);
  //   const totalPreloaded = 4;
    
  //   if (completedStudies.length === totalPreloaded && !currentAIAction.includes('All studies')) {
  //     if (completedStudies.length > 1) {
  //       addAiNotification(`All 2 preloaded studies ready for viewing`);
  //     }
  //   }
  // }, [loadingQueue, currentAIAction]);

  useEffect(() => {
    if (currentAIAction) {
      const clearTimer = setTimeout(() => {
        console.log('Auto-clearing stuck AI action:', currentAIAction);
        setCurrentAIAction('');
      }, 15000);

      return () => clearTimeout(clearTimer);
    }
  }, [currentAIAction]);

  const hasNext = caseQueue.length > 0;
  const hasPrevious = processedQueue.length > 0;

  const handleNext = () => {
    if (hasNext) {
      goToNextStudy();
    }
  };

  const handlePrevious = () => {
    if (hasPrevious) {
      const prevCase = processedQueue[processedQueue.length - 1];
      // onCaseChange(prevCase.studyId);
    }
  };

  const handleCaseSelect = (caseId) => {
    // onCaseChange(caseId);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        overlayRef.current &&
        !overlayRef.current.contains(event.target) &&
        queueButtonRef.current &&
        !queueButtonRef.current.contains(event.target)
      ) {
        setShowQueueOverlay(false);
      }
      
      if (
        notificationOverlayRef.current &&
        !notificationOverlayRef.current.contains(event.target) &&
        notificationButtonRef.current &&
        !notificationButtonRef.current.contains(event.target)
      ) {
        setShowNotifications(false);
      }
    };

    if (showQueueOverlay || showNotifications) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showQueueOverlay, showNotifications]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && (showQueueOverlay || showNotifications)) {
        setShowQueueOverlay(false);
        setShowNotifications(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showQueueOverlay, showNotifications]);

  useEffect(()=> {
    console.log("show queue overlay", showQueueOverlay);
  },[showQueueOverlay])

  const currentStudyReportStatus = currentStudy ? getReportStatusConfig(currentStudy.reportDoneStatus) : getReportStatusConfig(6);

  return (
    <>
      {showNotifications && (
        <div ref={notificationOverlayRef} className="notifications-overlay">
          <div className="notifications-header">
            <div className="notifications-header-content">
              <Text
                size={200}
                weight="semibold"
                className="notifications-title"
              >
                Notifications
              </Text>
              <Button
                icon={<Dismiss24Regular />}
                size="extra-small"
                appearance="transparent"
                className="status-bar-button notifications-close-btn"
                onClick={() => setShowNotifications(false)}
                aria-label="Close notifications"
              />
            </div>
          </div>

          <div className="notifications-list">
            {allNotifications.map((notification) => (
              <div key={notification.id} className="notification-item">
                <div className="notification-header">
                  <div className={`notification-type ${notification.type}`}>
                    {notification.type}
                  </div>
                  <Text size={100} className="notification-time">
                    {notification.time}
                  </Text>
                </div>
                <Text size={200} className="notification-message">
                  {notification.message}
                </Text>
              </div>
            ))}

            {allNotifications.length === 0 && (
              <div className="no-notifications">
                <Text size={200}>No notifications</Text>
              </div>
            )}
          </div>
        </div>
      )}

      {showQueueOverlay && (
        <div ref={overlayRef} className="queue-overlay">
          <div className="queue-header">
            <div className="queue-header-content">
              <div className="queue-header-left">
                <Text size={200} weight="semibold" className="queue-title">
                  Case Queue
                </Text>
                <div className="queue-count">{filteredCases.length} cases</div>
              </div>

              <div className="queue-header-right">
                {/* <Menu>
                  <MenuTrigger>
                    <Button
                      icon={<Filter24Regular />}
                      size="extra-small"
                      appearance="transparent"
                      className="status-bar-button queue-filter-btn"
                      aria-label="Filter cases"
                    />
                  </MenuTrigger>
                  <MenuPopover>
                    <MenuList>
                      <MenuItem onClick={() => setFilter("all")}>
                        All Cases ({caseQueue.length})
                      </MenuItem>
                      <MenuItem onClick={() => setFilter("normal")}>
                        Normal Cases
                      </MenuItem>
                      <MenuItem onClick={() => setFilter("abnormal")}>
                        Abnormal Cases
                      </MenuItem>
                    </MenuList>
                  </MenuPopover>
                </Menu> */}

                <Button
                  icon={<Dismiss24Regular />}
                  size="extra-small"
                  appearance="transparent"
                  className="status-bar-button queue-close-btn"
                  onClick={() => setShowQueueOverlay(false)}
                  aria-label="Close queue"
                />
              </div>
            </div>
          </div>

          <div className="queue-tiles-area">
            <div className="queue-tiles-scroll">
              <div className="queue-tiles-container">
                {filteredCases.map((dicomCase) => (
                  <div key={dicomCase.studyId} className="queue-tile-wrapper">
                    <CaseTile
                      case={dicomCase}
                      isActive={dicomCase.studyId === currentStudy?.studyId}
                      onClick={() => handleCaseSelect(dicomCase.studyId)}
                    />
                  </div>
                ))}
              </div>

              {filteredCases.length === 0 && (
                <div className="no-cases">
                  <Text size={200}>No cases match the current filter</Text>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="status-bar">
        <div className="status-bar-left">
          {/* Navigation Controls */}
          {/* <Tooltip content="Previous Case" relationship="label">
            <Button
              icon={<ChevronLeft24Regular />}
              size="extra-small"
              appearance="transparent"
              disabled={!hasPrevious}
              onClick={handlePrevious}
              className={`status-bar-button nav-button ${!hasPrevious ? 'disabled' : ''}`}
              aria-label="Previous case"
            />
          </Tooltip> */}

          <div className="case-counter">
            <Text size={200} className="counter-text">
              {currentNumber} / {totalStudies}
            </Text>
          </div>

          {/* <CustomToolTipHost content="Next Case" relationship="label">
            <Button
              icon={<ChevronRight24Regular />}
              size="extra-small"
              appearance="transparent"
              disabled={!hasNext}
              onClick={handleNext}
              className={`status-bar-button nav-button ${
                !hasNext ? "disabled" : ""
              }`}
              aria-label="Next case"
            />
          </CustomToolTipHost> */}

          <CustomToolTipHost content="Case Queue" relationship="label">
            <Button
              ref={queueButtonRef}
              icon={<Grid24Regular />}
              size="extra-small"
              appearance="transparent"
              onClick={() => setShowQueueOverlay(!showQueueOverlay)}
              onMouseEnter={() => setShowQueueOverlay(true)}
              className={`status-bar-button queue-button ${
                showQueueOverlay ? "active" : ""
              }`}
              aria-label="Toggle case queue"
              aria-expanded={showQueueOverlay}
            />
          </CustomToolTipHost>

          {currentStudy && (
            <div className="current-case-details">
              <div className="current-case-modality">
                {currentStudy.modality}
              </div>
              <div className={`current-case-status ${currentStudyReportStatus.className}`}>
                {currentStudyReportStatus.label}
              </div>

              <div className="current-case-patient">
                <Text size={100} className="patient-text">
                  {/* {currentStudy.patientId} • {currentStudy.patientName} */}

                  <CustomToolTipHost content={`PId: ${currentStudy.patientId}`}>
                    <div className="ellipsis-text">
                      {currentStudy.patientId}
                    </div>
                  </CustomToolTipHost>
                  <span className="separator"> | </span>
                  <CustomToolTipHost content={`Name: ${currentStudy.patientName}`}>
                    <div className="ellipsis-text">
                      {currentStudy.patientName}
                    </div>
                  </CustomToolTipHost>
                </Text>
              </div>
              {roles[0]=== USERTYPES.SUPERVISOR && <>
                <CustomToolTipHost content="Edit Patient">
                  <Button
                    icon={<Edit24Regular />}
                    size="extra-small"
                    appearance="transparent"
                    onClick={() => {
                      setShowEditPatientModal(true);
                    }}
                    className="status-bar-button edit-button"
                    aria-label="Edit study"
                  />
                </CustomToolTipHost>
                <CustomToolTipHost content="Close Study">
                  <Button
                    icon={<DismissCircle24Regular />}
                    size="extra-small"
                    appearance="transparent"
                    onClick={() => {
                      setShowCloseStudyModal(true);
                    }}
                    className="status-bar-button edit-button"
                    aria-label="Close study"
                  />
                </CustomToolTipHost>
                {!qcOutsourceLogin &&
                  <div className="current-case-patient">
                    <Text size={100} className="patient-text">
                      <CustomToolTipHost content={`Client: ${currentStudy.clientName}`}>
                        <div className="ellipsis-text">
                          {currentStudy.clientName}
                        </div>
                      </CustomToolTipHost>
                      <span className="separator"> | </span>
                      <CustomToolTipHost content={`Radiologist: ${currentStudy.radiologistName}`}>
                        <div className="ellipsis-text">
                          {currentStudy.radiologistName}
                        </div>
                      </CustomToolTipHost>
                    </Text>
                  </div>
                }
                </>
              }
              {roles[0]=== USERTYPES.RADIOLOGIST && currentStudy.clientName && <>
                <div className="current-case-patient">
                  <Text size={100} className="patient-text">
                    <span className="separator"> | </span>
                    <CustomToolTipHost content={`Client: ${currentStudy.clientName}`}>
                      <div className="ellipsis-text">
                        {currentStudy.clientName}
                      </div>
                    </CustomToolTipHost>
                  </Text>
                </div>
                </>
              }
            </div>
          )}
        </div>

        <div className="status-bar-middle">
          <CustomToolTipHost content={currentAIAction || "AI Assistant Ready"} relationship="label">
            <div className={`ai-status ${currentAIAction ? "active" : "ready"}`}>
              <Bot24Regular className="ai-icon" />
              {currentAIAction ? (
                <>
                  <span className="ai-action-text">{currentAIAction}</span>
                  <div className="ai-dots">
                    {React.createElement(Circle24Filled, {
                      className: "ai-dot",
                      style: { animationDelay: "0ms" },
                    })}
                    {React.createElement(Circle24Filled, {
                      className: "ai-dot",
                      style: { animationDelay: "150ms" },
                    })}
                    {React.createElement(Circle24Filled, {
                      className: "ai-dot",
                      style: { animationDelay: "300ms" },
                    })}
                  </div>
                </>
              ) : (
                <span className="ai-status-text">Ready</span>
              )}
            </div>
          </CustomToolTipHost>
        </div>

        <div className="status-bar-right">
          <div className="current-case-patient">
            <Text size={100} className="patient-text">
              <CustomToolTipHost content={`SessionId: ${sessionId}`}>
                {sessionId}
              </CustomToolTipHost>
            </Text>
          </div>
          
          <CustomToolTipHost content="Notifications" relationship="label">
            <div className="notification-button-wrapper">
              <Button
                ref={notificationButtonRef}
                icon={<Alert24Regular />}
                size="extra-small"
                appearance="transparent"
                onClick={() => setShowNotifications(!showNotifications)}
                className={`status-bar-button notification-button ${
                  showNotifications ? "active" : ""
                }`}
                aria-label="Toggle notifications"
                aria-expanded={showNotifications}
              />
              {unreadCount > 0 && (
                <div className="notification-badge">{unreadCount}</div>
              )}
            </div>
          </CustomToolTipHost>

          {/* <CustomToolTipHost content={isDarkTheme ? "Switch to Light Theme" : "Switch to Dark Theme"} relationship="label">
            <Button
              icon={
                isDarkTheme ? (
                  <WeatherSunny24Regular />
                ) : (
                  <WeatherMoon24Regular />
                )
              }
              size="extra-small"
              appearance="transparent"
              onClick={onThemeToggle}
              className="status-bar-button theme-button"
              aria-label={
                isDarkTheme ? "Switch to light theme" : "Switch to dark theme"
              }
            />
          </CustomToolTipHost> */}

          <CustomToolTipHost content="Close Session" relationship="label">
            <Button
              icon={<Dismiss24Regular />}
              size="extra-small"
              appearance="transparent"
              onClick={onClose}
              className="status-bar-button close-button"
              aria-label="Close viewer"
            />
          </CustomToolTipHost>
        </div>

        {showEditPatientModal && 
          <EditPatientModal 
            setShowEditPatientModal={setShowEditPatientModal} 
            showEditPatientModal={showEditPatientModal} 
            studyId={currentStudy.studyId} 
            patientName={currentStudy.patientName} 
            patientId={currentStudy.patientId} 
            data={currentData} 
            blitzSession={sessionId}
            handleCurrentStudyUpdate={handleCurrentStudyUpdate}/>
        }

        {showCloseStudyModal && 
          <CloseStudy 
            showCloseStudyModal={showCloseStudyModal} 
            setShowCloseStudyModal={setShowCloseStudyModal} 
            studyId={currentStudy.studyId} 
            patientName={currentStudy.patientName} 
            patientId={currentStudy.patientId}
            blitzSession={sessionId}
            />
        }
      </div>
    </>
  );
};

export default StatusBar;